<template>
  <nav
    v-outside-click="onClose"
    :class="b()"
    :style="style"
  >
    <div
      ref="level0"
      :class="b('level', { level: 0 })"
    >
      <div>
        <ul
          v-if="level0?.pages?.length"
          :class="b('list')"
        >
          <li
            v-for="page in level0.pages"
            :key="page.id"
            :class="b('item')"
          >
            <a
              :href="page.uri"
              :target="page.target"
              :title="page.title"
              :class="b('link', getLinkBemAttributes(page, 0))"
              :role="!page.uri && 'button'"
              :rel="page.target === '_blank' ? 'noopener noreferrer' : null"
              @click="onClick($event, 1, page)"
            >
              {{ page.label }}
            </a>
          </li>
        </ul>
      </div>
    </div>

    <transition name="slide-in-">
      <div
        v-show="showSubLevels"
        ref="subLevels"
        :class="b('sub-navigation-wrapper')"
      >
        <div
          v-for="(item, index) in subLevels"
          :key="item.id"
          :ref="`level${index + 1}`"
          :class="b('level', getLevelBemAttributes(index + 1))"
          :style="!index && !$viewport.isLg && { paddingLeft: `${horizontalAnchorPosition}px` }"
        >
          <button
            :class="b('close-button')"
            :aria-label="$t('c-main-navigation.closeButtonTitle')"
            type="button"
            @click="onClose"
          >
            <e-icon-sprite
              icon="i-close"
              size="20"
            />
          </button>

          <e-scrollable>
            <ul
              v-if="item.pages.length"
              :class="b('list')"
            >
              <li
                v-for="page in item.pages"
                :key="page.id"
                :class="b('item')"
              >
                <a
                  :href="page.uri"
                  :target="page.target"
                  :title="page.title"
                  :class="b('link', getLinkBemAttributes(page, index + 1))"
                  :role="!page.uri && 'button'"
                  :rel="page.target === '_blank' ? 'noopener noreferrer' : null"
                  @click="onClick($event, index + 2, page)"
                >
                  {{ page.label }}
                  <e-icon-sprite
                    v-if="!index && page.linkStyle === 'alternative'"
                    icon="i-chevron-2--right"
                    size="15"
                  />
                </a>
              </li>
            </ul>
          </e-scrollable>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script>
  import useSessionStore from '@/stores/session';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import eScrollable from '@/elements/e-scrollable.vue';

  const TRANSITION_DURATION = 300;

  /**
   * Renders the main navigation for the application.
   */
  export default {
    name: 'c-main-navigation',

    components: {
      eIconSprite,
      eScrollable,
    },
    // mixins: [],

    // props: {},
    setup() {
      return {
        sessionStore: useSessionStore(),
      };
    },
    data() {
      return {
        /**
         * @type {Boolean} Determines if the subLevels should be visible.
         */
        showSubLevels: false,

        /**
         * @type {Number} Holds the width of the subNavigation
         */
        subLevelWidth: 0,

        /**
         * @type {Boolean} True if user recently navigated.
         */
        hasNavigated: false,

        /**
         * @type {Number} Horizontal anchor position for alignment of level 1 and 2.
         */
        horizontalAnchorPosition: 0,

        /**
         * @type {Number} Vertical anchor position for alignment of level 1 and 2.
         */
        verticalAnchorPosition: 0,

        /**
         * @type {Array.<Object>} Stores the currently active navigation stack.
         */
        navigationStack: [],

        /**
         * @type {Object} Stores the previously active navigation stack.
         */
        storedNavigationStack: [],

        /**
         * @type {Object} Stores transition states for navigation levels.
         */
        transitionStates: {
          level0: false,
          level1: false,
          level2: false,
          level3: false,
          level4: false,
        },
      };
    },

    computed: {
      /**
       * Returns dynamic CSS variables for the root element.
       *
       * @returns {Object}
       */
      style() {
        return {
          '--c-main-navigation__top': `${this.verticalAnchorPosition}px`,
          '--c-main-navigation__sub-level-width': `${this.subLevelWidth}px`,
        };
      },

      /**
       * Returns the first navigation level.
       *
       * @returns {Object}
       */
      level0() {
        return this.navigationStack[0];
      },

      /**
       * Returns an array of subNavigationLevels.
       *
       * @returns {Array.<Object>}
       */
      subLevels() {
        return this.navigationStack.slice(1);
      },
    },
    watch: {
      /**
       * Change visibility and size of sub navigation wrapper on sub level change.
       *
       * @param {Array.<Object>} levels - The new list of levels.
       */
      subLevels: {
        handler(levels) {
          this.showSubLevels = !!levels.length;

          this.$nextTick(() => {
            this.calculateSubNavigationWidth();
          });
        },
        deep: true,
      },
    },

    // beforeCreate() {},
    created() {
      this.updateMainNavigationStack(0, this.sessionStore.mainNavigation);
      this.setInitialNavigationStack(0, this.sessionStore.mainNavigation);
    },
    // beforeMount() {},
    mounted() {
      this.setVerticalAnchorPosition();

      window.addEventListener('scroll', this.setVerticalAnchorPosition, { passive: true });
      window.addEventListener('resizeend', this.onResize);

      this.$nextTick(() => {
        this.setHorizontalAnchorPosition();
      });
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      window.removeEventListener('scroll', this.setVerticalAnchorPosition);
      window.removeEventListener('resizeend', this.onResize);
    },
    // unmounted() {},

    methods: {
      /**
       * Sets initial navigation stack recursive.
       *
       * @param {Number} level - The given navigation level.
       * @param {Object} node - The given navigation node.
       */
      setInitialNavigationStack(level, node) {
        if (level > 4 || !node?.pages?.length) {
          return;
        }

        this.storedNavigationStack.push(node);

        const activePage = node.pages.find((page) => page.active);

        if (activePage) {
          this.setInitialNavigationStack(level + 1, activePage);
        }
      },

      /**
       * Click event handler.
       *
       * @param {Event} event - DOM Event.
       * @param {Number} level - Navigation level.
       * @param {Object} node - Navigation node.
       */
      onClick(event, level, node) {
        if (!node.pages.length) {
          return;
        }

        event.preventDefault();

        if (level === 1) {
          this.setVerticalAnchorPosition();
        }

        // Close current level.
        if (this.navigationStack[level] === node) {
          if (level === 1) {
            this.onClose();

            return;
          }

          this.navigationStack = this.navigationStack.slice(0, level);

          return;
        }

        // Restore previous state
        if (level === 1 && this.storedNavigationStack[1] === node && !this.hasNavigated) {
          this.restore();
          this.hasNavigated = true;

          return;
        }

        this.hasNavigated = true;

        // Open sibling
        if (level !== 1 && level === this.navigationStack.length - 1) {
          this.transitionStates[`level${level}`] = false;
          this.updateMainNavigationStack(level, node);

          return;
        }

        // Open next level.
        this.updateMainNavigationStack(level, node);
      },

      /**
       * Handles resize event.
       */
      onResize() {
        this.setVerticalAnchorPosition();
        this.setHorizontalAnchorPosition();

        if (this.$viewport.isLg) {
          this.calculateSubNavigationWidth();
        }
      },

      /**
       * Closes all open subNavigations.
       */
      onClose() {
        if (!this.showSubLevels) {
          return;
        }

        this.$refs.subLevels?.addEventListener('transitionend', this.resetNavigationStack, { once: true });
        // Safari fallback, which did not trigger 'transitionend'.
        this.$refs.subLevels?.addEventListener('transitioncancel', this.resetNavigationStack, { once: true });

        this.showSubLevels = false;
      },

      /**
       * Resets the navigation stack.
       */
      resetNavigationStack() {
        this.navigationStack = this.navigationStack.slice(0, 1);

        Object.keys(this.transitionStates).forEach((key) => {
          this.transitionStates[key] = false;
        });

        this.$refs.subLevels?.removeEventListener('transitionend', this.resetNavigationStack);
        this.$refs.subLevels?.removeEventListener('transitioncancel', this.resetNavigationStack);
      },

      /**
       * Restore previously active navigation stack.
       */
      restore() {
        this.storedNavigationStack.forEach((item, index) => {
          this.transitionStates[`level${index}`] = true;
        });

        this.navigationStack = this.storedNavigationStack;
      },

      /**
       * Updates navigation stack.
       *
       * @param {Number} level - The level of the given node.
       * @param {Object} node - The given navigation node.
       */
      updateMainNavigationStack(level, node) {
        const transitionState = this.transitionStates[`level${level}`];

        this.navigationStack = this.navigationStack.slice(0, level);
        this.transitionStates[`level${level}`] = false;

        this.navigationStack.push(node);

        setTimeout(
          () => {
            this.transitionStates[`level${level}`] = true;
          },
          transitionState ? TRANSITION_DURATION : 0,
        );
      },

      /**
       * Sets horizontal anchor position based on level 1 x-axis position.
       */
      setHorizontalAnchorPosition() {
        const { level0 } = this.$refs;

        if (level0) {
          this.horizontalAnchorPosition = level0.getBoundingClientRect()?.left || 0;
        }
      },

      /**
       * Sets vertical anchor position based on header bottom position.
       */
      setVerticalAnchorPosition() {
        this.verticalAnchorPosition = document.querySelector('header')?.getBoundingClientRect().bottom || 0;
      },

      /**
       * Checks if the given page should be active.
       *
       * @param {Object} page - A page definition.
       * @param {Number} index - Index of the navigationStack.
       *
       * @returns {Boolean}
       */
      isPageActive(page, index) {
        return (
          (!this.hasNavigated && this.storedNavigationStack.length && page.active) ||
          page === this.navigationStack[index + 1]
        );
      },

      /**
       * Returns an object of bem attributes for the navigation links.
       *
       * @param {Object} page - A page definition.
       * @param {Number} index - Index of the navigationStack.
       *
       * @returns {Object}
       */
      getLinkBemAttributes(page, index) {
        return {
          isActive: this.isPageActive(page, index),
          isHighlighted: page.highlighted,
          linkStyle: page.linkStyle,
        };
      },

      /**
       * Returns an object of bem attributes for the navigation level element.
       *
       * @param {Number} index - Index of the navigationStack.
       *
       * @returns {Object}
       */
      getLevelBemAttributes(index) {
        return {
          level: index,
          isSubNavigation: true,
          isTransitioned: this.transitionStates[`level${index}`],
        };
      },

      /**
       * Calculates the with of the subNavigation.
       */
      calculateSubNavigationWidth() {
        this.subLevelWidth = this.$refs.subLevels?.offsetWidth || 0;
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:math';
  @use 'sass:map';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .c-main-navigation {
    $this: &;

    $c-main-navigation-z-index: map.get(variables.$z-index, 'navigation');
    $commerce-width--level-1: 527.78px;
    $commerce-width--level-2: 341.4067px;
    $commerce-width--level-3: 341.4067px;
    $commerce-width--level-4: 390px;
    $commerce-padding--level-1: 233.33px;

    &__list {
      @extend %list-reset;
    }

    &__link {
      display: flex;
      align-items: center;
      outline: none;
      cursor: pointer;

      .e-icon-sprite {
        width: functions.rem(variables.$spacing--15);
        height: functions.rem(variables.$spacing--15);
        margin: functions.rem(3px 0 0 variables.$spacing--5);
      }
    }

    &__sub-navigation-wrapper {
      position: fixed;
      top: var(--c-main-navigation__top, 0);
      right: 0;
      bottom: 0;
      left: 0;
      display: flex;
      pointer-events: none; // Enables support for outside click.

      @include mixins.media(lg) {
        right: calc(50% + 800px - var(--c-main-navigation__sub-level-width, 0px));
        left: auto;
      }

      > #{$this}__level {
        pointer-events: all;
      }
    }

    &__level--level-0 {
      #{$this}__list {
        display: flex;
        flex-wrap: wrap;
        max-height: 1.5em; // Keep in sync with line-height!
        overflow: hidden;
      }

      #{$this}__link {
        @include mixins.font(variables.$font-size--16);

        color: variables.$color-grayscale--100;
        font-family: variables.$font-family--text;
        letter-spacing: -0.03em;

        @include mixins.media(md) {
          @include mixins.font(variables.$font-size--18);
        }
      }

      #{$this}__link:hover,
      #{$this}__link:focus {
        color: variables.$color-primary--2;
      }

      #{$this}__item {
        flex: 0 0 auto;
        margin-left: functions.rem(variables.$spacing--80);
        white-space: nowrap;

        &:first-child {
          margin-left: 0;
        }
      }
    }

    &__level--is-sub-navigation {
      position: relative;
      padding: functions.rem(87px variables.$spacing--25 87px 75px);
      background-color: variables.$color-grayscale--930;

      .e-scrollable {
        padding-right: variables.$spacing--35;
      }
    }

    &__level--level-1 {
      z-index: $c-main-navigation-z-index + 3;
      width: 33%; // We don't want to use percentage() so the sum of the levels will be 100%.
      border-right: 1px solid variables.$color-grayscale--900;
      transition: opacity variables.$transition-duration--300;

      @include mixins.media(lg) {
        width: $commerce-width--level-1;
        padding-left: $commerce-padding--level-1;
      }

      &::before {
        @include mixins.media(lg) {
          position: absolute;
          top: 0;
          right: 100%;
          display: block;
          content: '';
          width: 25vw;
          height: 100vh;
          background-color: variables.$color-grayscale--930;
        }
      }

      #{$this}__item {
        margin-bottom: functions.rem(variables.$spacing--55);
      }

      #{$this}__link {
        @extend %link--dark-blue;
        @include mixins.font(variables.$font-size--24, 30px, variables.$font-weight--semi-bold);

        font-family: variables.$font-family--header;
        letter-spacing: unset;
      }
    }

    &__level--level-2,
    &__level--level-3,
    &__level--level-4 {
      border-right: 1px solid variables.$color-grayscale--900;
      transition-duration: variables.$transition-duration--300;
      transition-property: left, transform;

      #{$this}__item {
        margin-bottom: functions.rem(variables.$spacing--25);
      }

      #{$this}__link {
        @extend %link--dark-blue;
        @include mixins.font(variables.$font-size--18);
      }
    }

    &__level--level-2 {
      z-index: $c-main-navigation-z-index + 2;
      width: 21%;
      transform: translateX(-100%);

      @include mixins.media(lg) {
        width: $commerce-width--level-2;
      }
    }

    &__level--level-2 &__link--is-highlighted {
      font-weight: variables.$font-weight--semi-bold;
    }

    &__level--level-3 {
      z-index: $c-main-navigation-z-index + 1;
      width: 21%;
      transform: translateX(-100%);

      @include mixins.media(lg) {
        width: $commerce-width--level-3;
      }
    }

    &__level--level-4 {
      z-index: $c-main-navigation-z-index;
      width: 25%;
      border-right: 0;
      transform: translateX(-100%);

      @include mixins.media(lg) {
        width: $commerce-width--level-4;
      }
    }

    &__level--level-1 &__link--link-style-alternative {
      @extend %link--dark-blue;
      @include mixins.font(variables.$font-size--18, variables.$line-height--20, variables.$font-weight--regular);

      font-family: variables.$font-family--text;
    }

    &__level--is-transitioned {
      opacity: 1;
      transform: translateX(0);
    }

    &__level &__link--is-active {
      color: variables.$color-primary--2;
    }

    &__close-button {
      @extend %button-reset;

      position: absolute;
      top: functions.rem(variables.$spacing--25);
      right: functions.rem(variables.$spacing--25);
      display: none;
      color: variables.$color-primary--1;

      #{$this}__sub-navigation-wrapper :last-child & {
        display: block;
      }

      .e-icon-sprite {
        width: functions.rem(variables.$spacing--20);
        height: functions.rem(variables.$spacing--20);
      }

      &:hover,
      &:focus {
        color: variables.$color-primary--2;
      }
    }

    .slide-in--enter-active,
    .slide-in--leave-active {
      transition-duration: variables.$transition-duration--500;
      transition-property: opacity, right;
    }

    .slide-in--enter-from,
    .slide-in--leave-to {
      opacity: 0;

      @include mixins.media(lg) {
        right: 100%; // stylelint-disable-line length-zero-no-unit
        opacity: 1;
      }
    }
  }
</style>
